/* ==========================================================================
   The visit itself

   Between "you were chosen" and "it is done" there is the bit that actually
   matters: somebody standing in a house with a list. This is that screen.

   It is built for one hand, mid-work: the address at the top so it can be
   handed to a driver, the full list with the home's own notes, a tick per job,
   and somewhere to say what happened to a particular one -- "the linens were
   not dry, I had to run the dryer again and it was not finished when I left".
   That note belongs against the linens, not lost in a chat.
   ========================================================================== */

import { useState } from 'react';
import type { Booking } from '../../types';
import { Icon } from '../../components/Icon';
import { Card, Divider, Note, SectionHead } from '../../components/ui';
import { BOOKINGS } from '../../data/seed';
import { byId, house, jobLabel, shortName, townLabel } from '../../lib/domain';
import { endTime, fmtTime, money } from '../../lib/time';
import { t, tf } from '../../lib/i18n';
import { completeBooking, openChat, setJobState, state } from '../../store';
import { PhotoStrip } from '../../components/Photos';

export function JobInProgress() {
  const b = byId(BOOKINGS, state.params.id ?? '');
  if (!b) return null;
  const h = house(b.houseId);

  const doneCount = b.jobState.filter((j) => j.done).length;
  const total = b.jobIds.length;
  const allDone = total > 0 && doneCount === total;

  return (
    <div className="screen-pad">
      <Card className="tint">
        <div className="row between">
          <div className="col">
            <span className="sub tight">{h.displayName}</span>
            <b style={{ fontSize: 19 }}>
              {tf('tx_n_of_m_done', { n: doneCount, m: total })}
            </b>
          </div>
          <div className="col" style={{ textAlign: 'right' }}>
            <span className="sub tight">{fmtTime(b.start)}–{endTime(b.start, b.hours)}</span>
            <span className="rate" style={{ fontSize: 20 }}>{money(b.rate * b.hours)}</span>
          </div>
        </div>
        <div className="matchbar">
          <i style={{ width: total ? (doneCount / total) * 100 + '%' : '0%' }} />
        </div>
      </Card>

      <Card>
        <SectionHead title={t('h_where')} />
        {h.address ? (
          <>
            <b style={{ fontSize: 16 }}>{h.address}</b>
            <span className="sub">{townLabel(h)}</span>
            <a
              className="btn ghost block"
              href={`https://maps.google.com/?q=${encodeURIComponent([h.address, townLabel(h)].filter(Boolean).join(', '))}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Icon name="pin" /> {t('ip_maps')}
            </a>
          </>
        ) : (
          <Note warm>
            {t('ip_noaddress')}
          </Note>
        )}
        <button
          type="button"
          className="btn quiet block"
          onClick={() => openChat({ houseId: h.id })}
        >
          {t('b_message')} {shortName(h.displayName)}
        </button>
      </Card>

      {b.onceNote && (
        <Card className="warm">
          <SectionHead title={t('h_justthistime')} />
          <span className="sub">{b.onceNote}</span>
        </Card>
      )}

      {/* Whatever the home attached to this visit. Read-only: a cleaner
          cannot add a picture anywhere in Ozeret. */}
      <PhotoStrip kind="booking" ownerId={b.id} label={t('ip_pictures')} />

      <SectionHead title={t('h_thelist')} />
      {b.jobIds.map((jobId) => (
        <JobRow key={jobId} booking={b} jobId={jobId} />
      ))}

      <Card className={allDone ? 'tint' : ''}>
        {allDone ? (
          <span className="sub">{t('ip_allticked')}</span>
        ) : (
          <span className="sub">
            {t('tx_finish_without')}
          </span>
        )}
        <button
          type="button"
          className="btn lg block"
          onClick={() => completeBooking(b.id)}
        >
          {t('b_finish')}
        </button>
      </Card>
    </div>
  );
}

/** One job: tick it, or say something about it. */
function JobRow({ booking, jobId }: { booking: Booking; jobId: string }) {
  const st = booking.jobState.find((j) => j.jobId === jobId);
  const done = st?.done ?? false;
  const savedNote = st?.note ?? '';

  const h = house(booking.houseId);
  const homeNote = h.jobList.find((j) => j.jobId === jobId)?.note ?? '';

  const [writing, setWriting] = useState(false);
  const [draft, setDraft] = useState(savedNote);

  return (
    <Card className={done ? 'flat' : ''}>
      <div className="row top">
        <button
          type="button"
          className={'tickbox' + (done ? ' on' : '')}
          aria-pressed={done}
          aria-label={done ? t('tx_mark_not_done') : t('tx_mark_done')}
          onClick={() => setJobState(booking.id, jobId, { done: !done })}
        >
          {done && <Icon name="check" />}
        </button>
        <div className="col grow">
          <b style={{ fontSize: 15, textDecoration: done ? 'line-through' : 'none',
                      color: done ? 'var(--muted)' : 'var(--ink)' }}>
            {jobLabel(jobId)}
          </b>
          {homeNote && <span className="sub">{homeNote}</span>}
        </div>
      </div>

      {savedNote && !writing && (
        <>
          <Divider />
          <div className="col" style={{ gap: 3 }}>
            <span className="sub tight" style={{ fontWeight: 700, color: 'var(--bergamot-deep)' }}>
              {t('ip_yousaid')}
            </span>
            <span className="sub">{savedNote}</span>
          </div>
        </>
      )}

      {writing ? (
        <>
          <textarea
            className="textarea"
            autoFocus
            placeholder={t('ip_whathappened')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn sm grow"
              onClick={() => {
                setJobState(booking.id, jobId, { note: draft });
                setWriting(false);
              }}
            >
              {t('b_save')}
            </button>
            <button
              type="button"
              className="btn quiet sm"
              onClick={() => { setDraft(savedNote); setWriting(false); }}
            >
              {t('b_cancel')}
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => { setDraft(savedNote); setWriting(true); }}
        >
          {savedNote ? t('tx_edit_said') : t('tx_say_something')}
        </button>
      )}
    </Card>
  );
}
